// src/pages/comisiones/ComisionesPage.tsx

import React from "react";

const fmt = (n: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);

export default function ComisionesPage() {
  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          <i
            className="bi bi-receipt me-2"
            style={{ color: "var(--fg-green)", fontSize: "1.2rem" }}
          />
          Comisiones MVZ
        </h1>
        <p className="page-subtitle">
          Las comisiones se consultan por MVZ desde la sección{" "}
          <strong>Wallets MVZ</strong>. Aquí se explica el modelo de cobro.
        </p>
      </div>

      {/* Info model */}
      <div className="fg-card mb-4">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i
              className="bi bi-info-circle me-2"
              style={{ color: "var(--fg-green)" }}
            />
            Modelo de comisiones Feriagana
          </div>
        </div>
        <div style={{ padding: "20px 22px" }}>
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div
                style={{
                  background: "rgba(0,107,76,0.06)",
                  border: "1px solid rgba(0,107,76,0.15)",
                  borderRadius: 12,
                  padding: 20,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: 900,
                    color: "var(--fg-green)",
                  }}
                >
                  10%
                </div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Comisión por servicio
                </div>
                <p
                  style={{
                    fontSize: "0.82rem",
                    color: "var(--fg-muted)",
                    margin: 0,
                  }}
                >
                  Feriagana cobra el 10% del valor de cada servicio finalizado
                  vía CONTRAENTREGA.
                </p>
              </div>
            </div>
            <div className="col-12 col-md-8">
              <div style={{ fontSize: "0.875rem", lineHeight: 2 }}>
                <div className="fw-semibold mb-2">¿Cómo funciona?</div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {[
                    {
                      icon: "bi-1-circle-fill",
                      text: "El cliente solicita un servicio veterinario y paga CONTRAENTREGA al MVZ.",
                    },
                    {
                      icon: "bi-2-circle-fill",
                      text: "El MVZ finaliza el servicio y el cliente confirma. El sistema registra el ingreso en la wallet.",
                    },
                    {
                      icon: "bi-3-circle-fill",
                      text: "Se genera automáticamente una comisión PENDIENTE del 10% del valor del servicio.",
                    },
                    {
                      icon: "bi-4-circle-fill",
                      text: "El MVZ ve su saldo disponible menos la deuda de comisión. No puede retirar si tiene deuda.",
                    },
                    {
                      icon: "bi-5-circle-fill",
                      text: "El MVZ paga la comisión desde la app, que descuenta de su saldo disponible.",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      <i
                        className={`bi ${item.icon}`}
                        style={{
                          color: "var(--fg-green)",
                          fontSize: "1.1rem",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      />
                      <span
                        style={{
                          color: "var(--fg-muted)",
                          fontSize: "0.875rem",
                        }}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <hr style={{ margin: "24px 0", opacity: 0.1 }} />

          {/* Ejemplo */}
          <div style={{ fontWeight: 700, marginBottom: 12 }}>
            Ejemplo práctico
          </div>
          <div className="row g-3">
            {[
              {
                label: "Valor del servicio",
                value: fmt(100000),
                color: "var(--fg-text)",
              },
              {
                label: "Ingreso en wallet",
                value: fmt(100000),
                color: "var(--fg-green)",
              },
              {
                label: "Comisión Feriagana (10%)",
                value: `-${fmt(10000)}`,
                color: "var(--fg-red)",
              },
              {
                label: "Saldo neto disponible",
                value: fmt(90000),
                color: "var(--fg-green)",
              },
            ].map((item) => (
              <div className="col-6 col-md-3" key={item.label}>
                <div
                  style={{
                    background: "#f8fafb",
                    borderRadius: 10,
                    padding: "12px 14px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "var(--fg-muted)",
                      marginBottom: 6,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontSize: "1.15rem",
                      fontWeight: 800,
                      color: item.color,
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bloqueo automático */}
      <div className="fg-card">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i
              className="bi bi-shield-exclamation me-2"
              style={{ color: "var(--fg-orange)" }}
            />
            Bloqueo automático de wallets
          </div>
        </div>
        <div style={{ padding: "20px 22px", fontSize: "0.875rem" }}>
          <div
            className="alert alert-warning d-flex align-items-start gap-2 mb-3"
            style={{ borderRadius: 10 }}
          >
            <i className="bi bi-exclamation-triangle-fill mt-1" />
            <span>
              Si un MVZ acumula <strong>5 o más comisiones pendientes</strong>,
              su wallet se suspende automáticamente. No puede aceptar nuevos
              servicios mientras tenga deuda sin saldar.
            </span>
          </div>
          <div style={{ color: "var(--fg-muted)", lineHeight: 1.8 }}>
            El sistema revisa esto automáticamente cada 6 horas mediante el
            scheduler <code>WalletBloqueoScheduler</code>. Una vez el MVZ paga
            todas sus comisiones, la wallet vuelve al estado{" "}
            <strong>ACTIVA</strong> automáticamente.
          </div>
        </div>
      </div>
    </>
  );
}
